export const defaultIdLength = 16;
export type ID<Prefix extends string> = `${Prefix}${string}`;

const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";

function generateRandomId(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let result = "";
  for (let i = 0; i < length; i++) {
    result += alphabet[bytes[i] % alphabet.length];
  }
  return result;
}

export function generateId(length = defaultIdLength): string {
  return generateRandomId(length);
}

/**
 * Length for ids that only ever group log lines — request, cron-run, command
 * and process ids. Short enough to read at a glance in a log line, and
 * visually distinct from a 16-character row id body.
 *
 * 10 chars of this alphabet is 36^10 ≈ 3.7e15. At this system's volume
 * (~3,500 cron runs/day from the intervals in config.ts, plus requests at
 * ≪1 req/s) a month is ~5e5 ids, which by the birthday bound is a ~3e-5
 * chance of a single collision — and a collision here merges two runs in one
 * log filter, which you would spot immediately from the differing kind, name
 * and timestamps. That tolerance is why these can be shorter than row ids:
 * 6 chars would *not* do (collisions become near-certain within a month).
 */
export const shortIdLength = 10;

/**
 * An id for something transient that merely needs to be distinguishable — a
 * unit of work, a process. No `xxx_` prefix and no branded type: those are for
 * rows (CLAUDE.md). Never use this for a stored id.
 */
export function generateShortId(): string {
  return generateRandomId(shortIdLength);
}

export function makeIdGenerator<Prefix extends string>(
  prefix: Prefix,
  idLength = defaultIdLength,
): () => ID<Prefix> {
  return () => `${prefix}${generateRandomId(idLength)}`;
}
